import { useEffect, useId, useRef, type ReactNode } from "react";

/** One settings-bar control: a small label above its field, so every control in the bar shares one baseline. */
export function LabBarField({
  label,
  help,
  description,
  children,
}: {
  label: string;
  help?: ReactNode;
  /** Read with the field by screen readers. */
  description?: string;
  children: (ids: { inputId: string; describedBy?: string }) => ReactNode;
}) {
  const inputId = useId();
  const descriptionId = useId();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <label
          htmlFor={inputId}
          className="text-xs font-medium text-muted-foreground"
        >
          {label}
        </label>
        {help ? (
          <details className="relative">
            <summary
              aria-label={`About ${label}`}
              className="flex h-4 w-4 cursor-pointer list-none items-center justify-center rounded-full border border-border text-[10px] font-semibold text-muted-foreground hover:bg-secondary focus-visible:outline-2 focus-visible:outline-ring"
            >
              i
            </summary>
            <div className="absolute start-0 z-30 mt-1 w-72 rounded-md border border-border bg-card p-3 text-xs shadow-md">
              {help}
            </div>
          </details>
        ) : null}
      </div>
      {children({
        inputId,
        ...(description ? { describedBy: descriptionId } : {}),
      })}
      {description ? (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      ) : null}
    </div>
  );
}

/** A disclosure that closes on Escape (focus back on "More") and on a press outside it. */
export function LabMoreMenu({ children }: { children: ReactNode }) {
  const menu = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const el = menu.current;
      if (el?.open && !el.contains(event.target as Node)) {
        el.open = false;
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const el = menu.current;
      if (event.key === "Escape" && el?.open) {
        el.open = false;
        el.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);
  return (
    <details ref={menu} className="relative">
      <summary className="inline-flex h-9 cursor-pointer list-none items-center rounded-md border border-border bg-card px-3 text-sm font-medium hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
        More
      </summary>
      <div className="absolute end-0 z-30 mt-1 w-72 space-y-3 rounded-md border border-border bg-card p-3 shadow-md">
        {children}
      </div>
    </details>
  );
}
